import { useEffect, useMemo } from 'react';

import { isEqual } from 'lodash';

import { type Account } from '@pkg/graphql/codegen/graphql';
import { useFetchOnScroll, useGetActiveListingData } from '@pkg/hooks';
import { type MarketplaceCollectible } from '@pkg/types';

import { PROFILE_PAGE_WRAPPER_ID } from '@app/pages/ProfilePage/constants';
import { useNFTsFiltersStore, useWalletsQueryById } from '@app/store/zustand';

function splitAddress(address: string) {
    return address.split('.')?.[1];
}

// TODO: Consider breaking this down even further?
// This code could benefit from some polish. There is at least one
// strange issue where `nftEntries` re-orders itself at somepoint...
// it seems to try and group `chains` together in a sequence, and
// causes an abrupt 2nd render.

export function useNFTSectionData() {
    const { availableFilters, setAvailableFilters, filterHidden, filteredResultsCount, setFilteredResultsCount } =
        useNFTsFiltersStore();

    const { data, isLoading, fetchNextPage, hasNextPage, isFetching, ...rest } = useWalletsQueryById();

    useFetchOnScroll({
        hasNextPage,
        isLoading,
        isFetching,
        onCanFetch: fetchNextPage,
        elementId: PROFILE_PAGE_WRAPPER_ID,
        offset: 700,
    });

    const collectibleIds = data?.pages.flatMap(({ collectibles }) => collectibles.map((coll) => coll.id));

    const tokenIds = collectibleIds?.filter((id) => !id.includes('aptos'));

    /*
    const aptosSlugs =
        collectibleIds
            ?.map((id) => {
                if (id?.includes('aptos')) {
                    const arr = id.split('.');
                    if (arr) {
                        return `${arr[0]}.${arr[1]}.${arr[2]}`;
                    }
                }
            })
            .filter((s) => !!s) || [];

    const { data: aptosListingData } = useGetActiveListingData({ slugs: aptosSlugs as string[] });
    */

    const { data: activeListingData } = useGetActiveListingData({ tokenIds });

    const nftEntries = useMemo(() => {
        function getCollectiblesWithAccount(accounts: Account[]) {
            if (accounts?.length) {
                return accounts.reduce((acc: MarketplaceCollectible[], curr: Account) => {
                    const { collectibles, address } = curr;
                    const coll = collectibles?.map((c) => ({ ...c, account: address })) || [];

                    return [...acc, ...coll];
                }, []);
            }
        }

        const nfts = getCollectiblesWithAccount(data?.pages || []);

        const filtered = filterHidden
            ? nfts?.filter(({ hidden }) => (filterHidden === 'hidden' ? hidden : !hidden))
            : nfts;

        return filtered;
    }, [data?.pages, filterHidden]);

    const accounts = data?.pages.map((item) => splitAddress(item?.address));

    useEffect(() => {
        if (nftEntries && nftEntries.length && nftEntries.length !== filteredResultsCount) {
            setFilteredResultsCount(nftEntries.length);
        }
    }, [filteredResultsCount, nftEntries, setFilteredResultsCount]);

    useEffect(() => {
        if (accounts?.length && (!availableFilters || !isEqual(availableFilters?.wallets, accounts))) {
            setAvailableFilters({ wallets: accounts });
        }
    }, [accounts, availableFilters, setAvailableFilters]);

    return {
        accounts,
        nftEntries,
        isLoading,
        activeListingData,
        isFetching,
        ...rest,
    };
}
